import {
  ProgramManagementProfitLoss,
  ProgramManagementProgress,
  ProgramManagementProgressSet,
  ProgramManagementResource,
  ProgramManagementRow,
} from '../programManagement'

const sumProgress = (
  items: ProgramManagementProgress[]
): ProgramManagementProgress => ({
  total: items.reduce((s, p) => s + p.total, 0),
  scheduledCompleted: items.reduce((s, p) => s + p.scheduledCompleted, 0),
  completed: items.reduce((s, p) => s + p.completed, 0),
  delayed: items.reduce((s, p) => s + p.delayed, 0),
  preceding: items.reduce((s, p) => s + p.preceding, 0),
})

const sumProgressSet = (
  children: ProgramManagementRow[]
): ProgramManagementProgressSet | undefined => {
  const withProgress = children.filter(c => c.progress)
  if (withProgress.length === 0) return undefined
  return {
    deliverableCount: sumProgress(
      withProgress.map(c => c.progress!.deliverableCount)
    ),
    deliverableWorkload: sumProgress(
      withProgress.map(c => c.progress!.deliverableWorkload)
    ),
    taskCount: sumProgress(withProgress.map(c => c.progress!.taskCount)),
    taskWorkload: sumProgress(withProgress.map(c => c.progress!.taskWorkload)),
  }
}

const sumResource = (
  children: ProgramManagementRow[]
): ProgramManagementResource | undefined => {
  const withResource = children.filter(c => c.resource)
  if (withResource.length === 0) return undefined
  return {
    totalResource: withResource.reduce(
      (s, c) => s + c.resource!.totalResource,
      0
    ),
    investedResource: withResource.reduce(
      (s, c) => s + c.resource!.investedResource,
      0
    ),
    scheduledResource: withResource.reduce(
      (s, c) => s + c.resource!.scheduledResource,
      0
    ),
    actualResource: withResource.reduce(
      (s, c) => s + c.resource!.actualResource,
      0
    ),
  }
}

const sumProfitLoss = (
  children: ProgramManagementRow[]
): ProgramManagementProfitLoss | undefined => {
  const withProfitLoss = children.filter(c => c.profitLoss)
  if (withProfitLoss.length === 0) return undefined
  return {
    totalBudget: withProfitLoss.reduce(
      (s, c) => s + c.profitLoss!.totalBudget,
      0
    ),
    scheduledUsedBudget: withProfitLoss.reduce(
      (s, c) => s + c.profitLoss!.scheduledUsedBudget,
      0
    ),
    actualUsedBudget: withProfitLoss.reduce(
      (s, c) => s + c.profitLoss!.actualUsedBudget,
      0
    ),
    remainingBudget: withProfitLoss.reduce(
      (s, c) => s + c.profitLoss!.remainingBudget,
      0
    ),
    totalRevenue: withProfitLoss.reduce(
      (s, c) => s + c.profitLoss!.totalRevenue,
      0
    ),
    scheduledRevenue: withProfitLoss.reduce(
      (s, c) => s + c.profitLoss!.scheduledRevenue,
      0
    ),
    actualRevenue: withProfitLoss.reduce(
      (s, c) => s + c.profitLoss!.actualRevenue,
      0
    ),
    forecastRevenue: withProfitLoss.reduce(
      (s, c) => s + c.profitLoss!.forecastRevenue,
      0
    ),
  }
}

/**
 * フラットな行配列に対してボトムアップ集計を行う。
 * treeValue から親子関係を復元し、深い行から順に処理することで
 * 子の集計値が親に正しく伝播する。
 *
 * - progress: 自身に値がなければ直下の子から集計
 * - resource: 自身に値がなければ直下の子から集計
 * - profitLoss: 自身に値がなければ直下の子から集計
 */
export const aggregateTreeData = (
  rows: ProgramManagementRow[]
): ProgramManagementRow[] => {
  // treeValue から親子関係を構築
  const childrenByParentUuid = new Map<string, string[]>()
  for (const row of rows) {
    if (row.treeValue.length >= 2) {
      const parentUuid = row.treeValue[row.treeValue.length - 2]
      const siblings = childrenByParentUuid.get(parentUuid) ?? []
      siblings.push(row.uuid)
      childrenByParentUuid.set(parentUuid, siblings)
    }
  }

  // uuid → row のルックアップ（集計結果を格納する mutable コピー）
  const rowMap = new Map<string, ProgramManagementRow>(
    rows.map(r => [r.uuid, { ...r }])
  )

  // 深い行から順に処理（ボトムアップ）
  const sortedRows = [...rows].sort(
    (a, b) => b.treeValue.length - a.treeValue.length
  )

  for (const row of sortedRows) {
    const childUuids = childrenByParentUuid.get(row.uuid)
    if (!childUuids || childUuids.length === 0) continue

    const children = childUuids
      .map(uuid => rowMap.get(uuid))
      .filter((r): r is ProgramManagementRow => r !== undefined)

    const updated = rowMap.get(row.uuid)!
    if (!updated.progress) updated.progress = sumProgressSet(children)
    if (!updated.resource) updated.resource = sumResource(children)
    if (!updated.profitLoss) updated.profitLoss = sumProfitLoss(children)
  }

  // 元の配列順序を維持して返す
  return rows.map(r => rowMap.get(r.uuid) ?? r)
}
